import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  viewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { deburr } from 'lodash-es';
import { CheckboxModule } from 'primeng/checkbox';
import { ChevronDownIcon } from 'primeng/icons';
import { Popover, PopoverModule } from 'primeng/popover';

import { HideOnWindowScrollDirective } from '@design-system/directives/hide-on-window-scroll/hide-on-window-scroll.directive';
import { MInputSearchComponent } from '@design-system/components/m-input-search';
import { MTooltipComponent } from '@design-system/components/m-tooltip';
import {
  deriveMappedAttributesFromSelection,
  filterGroupedSections,
  isCombinationAvailable,
  normalizeMappedAttributes,
  resolveMappedSelections
} from '@design-system/components/m-grouped-dropdown/grouped-dropdown.util';
import {
  GroupedSelectionChange,
  GroupItem,
  GroupSection,
  MultiSelectMapping
} from '@design-system/components/m-grouped-dropdown/m-grouped-dropdown.types';

@Component({
  selector: 'm-grouped-dropdown',
  templateUrl: './m-grouped-dropdown.component.html',
  styleUrls: ['./m-grouped-dropdown.component.scss'],
  imports: [
    FormsModule,
    PopoverModule,
    CheckboxModule,
    ChevronDownIcon,
    HideOnWindowScrollDirective,
    MInputSearchComponent,
    MTooltipComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MGroupedDropdownComponent {
  readonly popover = viewChild<Popover>('popover');
  readonly searchInput = viewChild(MInputSearchComponent);

  readonly label = input('Conversion Type');
  readonly disabled = input(false);
  readonly placeholder = input('Select');
  readonly groups = input<GroupSection[]>([]);
  readonly mappings = input<MultiSelectMapping[]>([]);
  readonly selected = input<string[]>([]);
  /** Raw mapped checkbox picks (not expanded selected). */
  readonly displaySelected = input<Record<string, string[]>>({});
  /** Persisted mode so mapped UI survives recreate. */
  readonly selectionMode = input<'single' | 'mapped' | null>(null);
  readonly selectionChange = output<GroupedSelectionChange>();

  readonly isOpen = signal(false);
  readonly searchQuery = signal('');

  readonly mode = signal<'single' | 'mapped' | null>(null);
  readonly selectedValue = signal<string | null>(null);
  readonly selectedAttributes = signal<Record<string, string[]>>({});

  readonly bodyTooltipOptions = { appendTo: 'body' } as const;

  private lastEmitted: {
    selectedValues: string[];
    mode: 'single' | 'mapped' | null;
    selectedAttributes: Record<string, string[]> | null;
  } = { selectedValues: [], mode: null, selectedAttributes: null };

  private readonly normalizedQuery = computed(() =>
    deburr(this.searchQuery().trim().toLowerCase())
  );

  private readonly mappedCategories = computed(() =>
    this.groups()
      .filter((g) => g.type === 'mapped')
      .flatMap((g) => g.items)
  );

  readonly selectedSingleValue = computed(() =>
    this.mode() === 'single' ? this.selectedValue() : null
  );

  readonly displayValue = computed(() => {
    if (this.mode() === 'mapped') {
      const count = Object.values(this.selectedAttributes()).reduce(
        (total, values) => total + values.length,
        0
      );
      return count > 0 ? `${count} selected` : this.placeholder();
    }
    return this.selectedValue() ?? this.placeholder();
  });

  readonly filteredGroups = computed(() =>
    filterGroupedSections(this.groups(), this.normalizedQuery())
  );

  // Disabled mapped options for the current selection.
  private readonly disabledOptions = computed(() => {
    const mappings = this.mappings();
    const selectedAttributes = this.selectedAttributes();
    const disabled = new Map<string, Set<string>>();
    if (!mappings.length) {
      return disabled;
    }
    this.mappedCategories().forEach((category) => {
      const disabledValues = new Set<string>();
      (category.options ?? []).forEach((option) => {
        const optionValue = String(option.value);
        const checked = (selectedAttributes[category.value] ?? []).includes(
          optionValue
        );
        if (
          !checked &&
          !isCombinationAvailable(
            mappings,
            selectedAttributes,
            category.value,
            optionValue
          )
        ) {
          disabledValues.add(optionValue);
        }
      });
      if (disabledValues.size) {
        disabled.set(category.value, disabledValues);
      }
    });
    return disabled;
  });

  constructor() {
    effect(() => {
      const categories = this.mappedCategories();
      if (!categories.length) return;

      this.selectedAttributes.update((current) =>
        categories.every((c) => c.value in current)
          ? current
          : this.emptyMappedAttributes()
      );
    });

    effect(() => {
      // Don't reset in-progress picks while open.
      const selectedValues = this.selected();
      if (this.isOpen()) {
        return;
      }
      this.applySelectedValues(selectedValues);
    });
  }

  private applySelectedValues(selectedValues: string[]): void {
    if (!selectedValues.length) {
      this.mode.set(null);
      this.selectedValue.set(null);
      this.selectedAttributes.set(this.emptyMappedAttributes());
      this.lastEmitted = {
        selectedValues,
        mode: null,
        selectedAttributes: null
      };
      return;
    }

    const categories = this.mappedCategories();
    const keepMappedDisplay =
      this.selectionMode() === 'mapped' ||
      this.mode() === 'mapped' ||
      selectedValues.length > 1;

    if (!keepMappedDisplay || !categories.length) {
      this.mode.set('single');
      this.selectedValue.set(selectedValues[0] ?? null);
      this.selectedAttributes.set(this.emptyMappedAttributes());
      this.lastEmitted = {
        selectedValues,
        mode: 'single',
        selectedAttributes: null
      };
      return;
    }

    this.mode.set('mapped');
    this.selectedValue.set(null);
    this.selectedAttributes.set(
      this.resolveDisplayAttributes(selectedValues, categories)
    );
    this.lastEmitted = {
      selectedValues,
      mode: 'mapped',
      selectedAttributes: this.currentSelectedAttributes()
    };
  }

  /** Prefer displaySelected; else derive from effective values. */
  private resolveDisplayAttributes(
    selectedValues: string[],
    categories: GroupItem[]
  ): Record<string, string[]> {
    const display = this.displaySelected();
    const hasDisplay = Object.values(display).some(
      (values) => values.length > 0
    );
    if (hasDisplay) {
      return Object.fromEntries(
        categories.map((category) => [
          category.value,
          display[category.value] ?? []
        ])
      );
    }
    return deriveMappedAttributesFromSelection(
      selectedValues,
      this.mappings(),
      categories
    );
  }

  togglePanel(event: Event): void {
    if (!this.disabled()) {
      this.popover()?.toggle(event);
    }
  }

  onShow(): void {
    this.isOpen.set(true);
    setTimeout(() => this.searchInput()?.focus(), 0);
  }

  onHide(): void {
    this.isOpen.set(false);
    this.searchQuery.set('');

    // Expand mapped picks only when emitting on hide.
    if (this.mode() === 'mapped') {
      this.emitIfChanged();
    }
  }

  selectSingle(value: string): void {
    this.mode.set('single');
    this.selectedValue.set(value);
    this.selectedAttributes.set(this.emptyMappedAttributes());
    this.emitIfChanged();
    this.popover()?.hide();
  }

  isAttributeChecked(categoryValue: string, optionValue: string): boolean {
    return (this.selectedAttributes()[categoryValue] ?? []).includes(
      optionValue
    );
  }

  /** No mapping matches this option with current picks. */
  isAttributeDisabled(categoryValue: string, optionValue: string): boolean {
    return this.disabledOptions().get(categoryValue)?.has(optionValue) ?? false;
  }

  onAttributeToggle(
    categoryValue: string,
    optionValue: string,
    checked: boolean
  ): void {
    this.mode.set('mapped');
    this.selectedValue.set(null);

    const next = { ...this.selectedAttributes() };
    const current = next[categoryValue] ?? [];
    next[categoryValue] = checked
      ? [...current, optionValue]
      : current.filter((v) => v !== optionValue);
    this.selectedAttributes.set(next);
  }

  private emptyMappedAttributes(): Record<string, string[]> {
    return Object.fromEntries(
      this.mappedCategories().map((c) => [c.value, []])
    );
  }

  private emitIfChanged(): void {
    const selectedValues = this.currentSelection();
    const mode = this.mode() ?? 'single';
    const selectedAttributes =
      mode === 'mapped' ? this.currentSelectedAttributes() : null;
    // Dedup on values + mode + raw picks.
    if (
      this.sameSelection(selectedValues, this.lastEmitted.selectedValues) &&
      mode === this.lastEmitted.mode &&
      this.sameSelectedAttributes(
        selectedAttributes,
        this.lastEmitted.selectedAttributes
      )
    ) {
      return;
    }
    this.lastEmitted = { selectedValues, mode, selectedAttributes };
    this.selectionChange.emit({ selectedValues, selectedAttributes, mode });
  }

  /** Non-empty attribute picks. */
  private currentSelectedAttributes(): Record<string, string[]> {
    return Object.fromEntries(
      Object.entries(this.selectedAttributes()).filter(
        ([, values]) => values.length > 0
      )
    );
  }

  private sameSelectedAttributes(
    a: Record<string, string[]> | null,
    b: Record<string, string[]> | null
  ): boolean {
    if (a === b) {
      return true;
    }
    if (!a || !b) {
      return false;
    }
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length) {
      return false;
    }
    return aKeys.every((key) => this.sameSelection(a[key] ?? [], b[key] ?? []));
  }

  private sameSelection(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((value) => b.includes(value));
  }

  private currentSelection(): string[] {
    if (this.mode() === 'single') {
      return this.selectedValue() ? [this.selectedValue()!] : [];
    }
    if (this.mode() === 'mapped') {
      // Expand picks for emit; leave checkbox state raw.
      const normalized = normalizeMappedAttributes(
        this.mappings(),
        this.mappedCategories(),
        this.selectedAttributes()
      );
      return resolveMappedSelections(
        this.mappings(),
        this.mappedCategories(),
        normalized
      );
    }
    return [];
  }
}
